import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './Landing.module.css';
import OnyxxLogo from '../../../images/OnyxxLogo.jpeg';

const FEATURES = [
'10 Enterprise Questions',
'5 Minute Assessment',
'AI Readiness Score',
'Executive Report',
'Personalized Recommendations',
'AI Governance Insights'
];

export default function Landing({ onStart }) {
  // Only show the success popup if the lead form actually set this flag.
  const [showSuccess, setShowSuccess] = useState(
    () => localStorage.getItem('leadSubmitted') === 'true'
  );

  useEffect(() => {
    // Clear the flag once shown so it doesn't persist across future visits.
    if (showSuccess) {
      localStorage.removeItem('leadSubmitted');
    }
  }, [showSuccess]);

  const closeSuccess = () => setShowSuccess(false);

  return (
    <motion.div
      className={styles.landing}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
    >
      <div className={styles.glow} />

      <AnimatePresence>
        {showSuccess && (
          <motion.div
            className={styles.successOverlay}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={closeSuccess}
          >
            <motion.div
              className={styles.successModal}
              initial={{ opacity: 0, y: -20, scale: 0.96 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.96 }}
              transition={{ duration: 0.3 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className={styles.successClose}
                onClick={closeSuccess}
                aria-label="Close"
              >
                ✕
              </button>

              <div className={styles.successIcon}>✓</div>

              <div>
                <h3>Thank You!</h3>

                <p>
                  Your ONYXX Trial request has been submitted successfully.
                </p>

                <p>
                  Our AI Security Specialists will review your request and contact you shortly.
                  Before your demo, we'd like to understand your organization's AI maturity.
                </p>
              </div>

              <button
                type="button"
                className={`${styles.successCta} aira-button`}
                onClick={closeSuccess}
              >
                Continue
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.img
        src={OnyxxLogo}
        alt="ONYXX — Intelligence Meets AI Security"
        className={styles.logo}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
      />

      <motion.span
        className={styles.eyebrow}
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        AI Governance &amp; Security
      </motion.span>

      <motion.h1
        className={styles.title}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        ONYXX <span className="aira-gradient-text">AI Readiness</span> Assessment
      </motion.h1>

      <motion.p
        className={styles.subtitle}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
       Complete your complimentary AI Readiness Assessment and receive a personalized AI maturity score.
      </motion.p>

      <motion.p
        className={styles.description}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        Answer 10 carefully selected questions to evaluate your organization's readiness in AI
        Governance, Data Loss Prevention (DLP), Enterprise AI Adoption, AI Agents, and Security.
      </motion.p>

      <motion.div
        className={styles.featureGrid}
        initial="hidden"
        animate="visible"
        variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.45 } } }}
      >
        {FEATURES.map((feature) => (
          <motion.div
            key={feature}
            className={`${styles.featureCard} aira-card`}
            variants={{ hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } }}
          >
            <span className={styles.checkIcon}>✓</span>
            {feature}
          </motion.div>
        ))}
      </motion.div>

      <motion.button
        type="button"
        className={`${styles.ctaButton} aira-button`}
        onClick={onStart}
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65 }}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.98 }}
      >
        Start Assessment
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M5 12h14M12 5l7 7-7 7" />
        </svg>
      </motion.button>
    </motion.div>
  );
}